/**
 * Entity Actions — canonical mutation API.
 *
 * Unix-like primitives for entity operations with hybrid optimistic/simple
 * approach:
 *   - Optimistic: folder/hierarchy operations (immediate UI feedback).
 *   - Simple:    generic entity operations (wait for backend).
 *
 * All actions handle:
 *   - REST persistence via `entityService`.
 *   - Local-store dispatch via `entityRegistry` (through `upsertEntity` /
 *     `deleteEntityLocal` from `entityMutations`), which routes the entity to
 *     its owning scope (space / global / app).
 *   - Event emission (`entityEventService`, `worldEventsService`).
 *   - Error handling and rollback.
 *
 * Each action is a stand-alone async function; consumers should
 * `import { entityActions } from '../stores/entityActions'` and call e.g.
 * `entityActions.createEntity({...})`.
 */

import { BaseEntityType } from '../types';
import { entityService } from '../services/entityService';
import { entityEventService } from '../services/entityEventService';
import { emitEvent } from '../services/worldEventsService';
import { createFileLogger } from '../utils/logger';
import useSpaceStore from './spaceStore';
import { entityRegistry } from './entityRegistry';
import { upsertEntity, deleteEntityLocal } from './entityMutations';

const log = createFileLogger('ENTITY_ACTIONS');

// Returns the active space's Mongo entity ID — the correct spaceId for all
// entity operations (not the Y.js CRDT room key).
const getActiveSpaceId = (): string | undefined =>
  useSpaceStore.getState().activeSpace?.id;

// Lookup helpers — replace the legacy `get().entities.{find,filter}` calls.
const findById = (id: string): BaseEntityType | undefined =>
  entityRegistry.getById(id) as BaseEntityType | undefined;

/**
 * Snapshot every entity currently in the registry (across all scopes). Used by
 * composite operations (`groupEntities`, `ungroupEntities`) that need a stable
 * pre-mutation reference set for rollback. The returned array is detached from
 * the registry — subsequent upserts won't mutate it — but the entities inside
 * are shared references (which is fine since stores never mutate entities in
 * place).
 */
const snapshotAllEntities = (): BaseEntityType[] =>
  entityRegistry.dumpAll().flatMap((s) => s.entities) as BaseEntityType[];

export const entityActions = {
  // ========================================================================
  // OPTIMISTIC OPERATIONS (Folder/Hierarchy Management)
  // ========================================================================

  /**
   * Create a new folder with optimistic UI updates
   * Unix-like: mkdir
   */
  createFolder: async (name: string, parentId?: string, options?: { color?: string, spaceId?: string, edge_pos?: string }) => {
    const spaceId = options?.spaceId || getActiveSpaceId() || '';
    // Use crypto.randomUUID() for temp IDs so offline folders from different tabs never collide
    const tempId = `temp_${crypto.randomUUID()}`;

    log.info('[createFolder] ✅ spaceId resolved:', { spaceId, source: options?.spaceId ? 'caller' : 'activeSpace' });
    log.info('[createFolder] Creating folder with optimistic update:', { name, parentId, spaceId, tempId });

    // Optimistic: Add temp folder to state immediately via helper
    const tempFolder: BaseEntityType = {
      _id: tempId,
      id: tempId,
      name: name.trim(),
      entityType: 'Group',
      type: 'group',
      spaceId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      skeleton: {
        '@type': 'Group',
        childEntities: [],
        isExpanded: true
      },
      ...(options?.color ? { color: options.color } : {})
    };

    upsertEntity(tempFolder, { log: false, source: 'local', syncCRDT: false });

    // ── Offline path ───────────────────────────────────────────────────────
    if (!navigator.onLine) {
      const pendingFolder = { ...tempFolder, _pending: true } as any;
      const { writeEntityToYDoc } = await import('../stores/entitySystemYjsStore');
      const { enqueue } = await import('../services/offlineEntityQueue');

      writeEntityToYDoc(tempId, pendingFolder);
      // Update Zustand to show _pending flag
      upsertEntity(pendingFolder, { source: 'local', log: false, syncCRDT: false });
      enqueue({
        id: tempId,
        type: 'folder',
        params: { ...pendingFolder, parentId },
        parentId,
        queuedAt: Date.now(),
      });

      log.info(`[createFolder] 📴 Offline — queued folder with tempId: ${tempId.slice(-8)}`);
      return pendingFolder as BaseEntityType;
    }
    // ──────────────────────────────────────────────────────────────────────

    try {
      // ✅ CRITICAL: Resolve parentId if it's an llmId
      let resolvedParentId = parentId;
      if (parentId) {
        const { resolveEntityId } = await import('../systemCommands/entitySystem/entityIdResolver');
        const resolution = resolveEntityId(parentId);
        resolvedParentId = resolution.resolved;
        
        log.info('[createFolder] Resolved parent ID for groupIds:', {
          original: parentId,
          resolved: resolvedParentId,
          isLlmId: resolution.isLlmId
        });
      }
      
      // Create folder on backend (entityService.create handles org_id automatically)
      // ✅ ORGS V1: Get current org context
      const { getCurrentOrgId } = await import('../stores/dataspaceContextStore');
      const currentOrgId = getCurrentOrgId();
      
      const folderData: any = {
        entityType: 'Group',
        name: name.trim(),
        spaceId,
        skeleton: {
          '@type': 'Group',
          childEntities: [],
          isExpanded: true,
          groupIds: resolvedParentId ? [resolvedParentId] : []  // ✅ Use resolved _id!
        },
        created_at: new Date().toISOString(),
        org_id: currentOrgId || undefined,  // ✅ ORGS V1: Set org_id from current context
      };
      
      if (options?.color) {
        folderData.color = options.color;
      }
      if (options?.edge_pos) {
        folderData.edge_pos = options.edge_pos;
      }
      
      log.info('[createFolder] Creating folder with org_id:', { orgId: currentOrgId, folderName: name });
      
      const createdFolder = await entityService.create(folderData);
      const actualId = createdFolder._id || createdFolder.id;
      
      log.info('[createFolder] Folder created on backend:', { actualId, name: createdFolder.name });
      
      // Remove temp folder and add real one via helpers
      deleteEntityLocal(tempId, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      const normalized = upsertEntity(createdFolder, {
        log: true,  // Log the real entity
        source: 'local',
        syncCRDT: true  // Sync the real entity
      });
      
      // If parentId provided, add to parent's childEntities AND set child's groupIds
      // Note: resolvedParentId already computed above during groupIds setup
      if (resolvedParentId) {
        try {
          const parentFolder = await entityService.get(resolvedParentId);
          const currentChildren = parentFolder.skeleton?.childEntities || [];
          
          log.info('[createFolder] Found parent folder:', {
            parentId: resolvedParentId,
            parentName: parentFolder.name,
            currentChildCount: currentChildren.length
          });
          
          // Update parent's childEntities in backend
          await entityService.mergeUpdate(resolvedParentId, {
            skeleton: {
              childEntities: [...currentChildren, actualId]
            }
          });
          
          log.info('[createFolder] Updated parent folder childEntities:', {
            parentId: resolvedParentId,
            newChildCount: currentChildren.length + 1
          });
          
          // ✅ groupIds already set during folder creation above (line 67)
          // Just fetch fresh to confirm
          const updatedFolder = await entityService.get(actualId);
          
          // Update store with complete entity data
          upsertEntity(updatedFolder, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
          
          log.info('[createFolder] Added folder to parent:', { parentId: resolvedParentId, childId: actualId });
        } catch (parentError) {
          log.error('[createFolder] Failed to add to parent folder:', parentError);
        }
      }
      
      // Edge arrives via org Y.Doc broadcast — no REST refresh needed
      
      // Emit event for external consumers
      entityEventService.entityCreated(normalized, spaceId, {
        command: 'createFolder'
      });
      
      return normalized;
      
    } catch (error) {
      log.error('[createFolder] Failed to create folder:', error);
      
      // Rollback: Remove temp folder via helper
      deleteEntityLocal(tempId, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      throw error;
    }
  },

  /**
   * Move entity to different folder with optimistic UI
   * Unix-like: mv
   * ✅ SIMPLIFIED: Uses tree system only (no legacy groupIds/childEntities)
   */
  moveEntity: async (entityId: string, targetParentId: string | null) => {
    const spaceId = getActiveSpaceId();
    const entity = findById(entityId);
    
    if (!entity) {
      log.warn('[moveEntity] Entity not found:', entityId);
      return;
    }
    
    if (!spaceId) {
      log.warn('[moveEntity] No active space — cannot resolve tree root parent');
      return;
    }
    log.info('[moveEntity] ✅ treeRoot parent resolved:', { spaceId });
    
    // ✅ CRITICAL: Resolve targetParentId if it's an llmId
    let resolvedTargetParentId = targetParentId;
    if (targetParentId) {
      const { resolveEntityId } = await import('../systemCommands/entitySystem/entityIdResolver');
      const resolution = resolveEntityId(targetParentId);
      resolvedTargetParentId = resolution.resolved;
      
      log.info('[moveEntity] Resolved target parent ID:', {
        original: targetParentId,
        resolved: resolvedTargetParentId,
        isLlmId: resolution.isLlmId
      });
    }
    
    log.info('[moveEntity] Moving entity via tree system:', { 
      entityId: entityId.slice(-6), 
      targetParentId: resolvedTargetParentId?.slice(-6) || 'root',
      spaceId: spaceId.slice(-6)
    });
    
    // Optimistic: Update parentId immediately for UI responsiveness
    const updatedEntity = {
      ...entity,
      parentId: resolvedTargetParentId
    };
    
    upsertEntity(updatedEntity, {
      log: true,
      source: 'local',
      syncCRDT: false  // Don't sync to CRDT yet, wait for backend confirmation
    });
    
    try {
      // ✅ SINGLE SOURCE OF TRUTH: Use tree system only
      const { useDataspaceTreeStore } = await import('./dataspaceTreeStore');
      const treeStore = useDataspaceTreeStore.getState();
      const targetParent = resolvedTargetParentId || spaceId;
      // Insert at end: find the last sibling (excluding self) so afterIndex resolves to
      // the last position. Passing null would insert at the beginning instead.
      const allSiblings = treeStore.childrenByParentId.get(targetParent) || [];
      const siblingsWithoutSelf = allSiblings.filter(id => id !== entityId);
      const afterNodeId = siblingsWithoutSelf.length > 0 ? siblingsWithoutSelf[siblingsWithoutSelf.length - 1] : null;
      await treeStore.moveNode(entityId, targetParent, afterNodeId);
      
      log.info('[moveEntity] ✅ Entity moved successfully via tree system');
      
    } catch (error) {
      log.error('[moveEntity] Failed to move entity:', error);
      
      // Rollback via helper
      upsertEntity(entity, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      throw error;
    }
  },

  /**
   * Soft delete entity with optimistic UI
   * Unix-like: rm (marks as trashed, not permanent delete)
   */
  softDeleteEntity: async (entityId: string) => {
    const entity = findById(entityId);
    
    if (!entity) {
      log.warn('[softDeleteEntity] Entity not found:', entityId);
      return;
    }
    
    log.info('[softDeleteEntity] Soft deleting entity:', { entityId });
    
    // ✅ OPTIMISTIC: Mark entity as trashed in store immediately (for instant UI feedback)
    const trashedEntity = { ...entity, trashed: true };
    upsertEntity(trashedEntity, {
      log: false,  // Don't log optimistic update
      source: 'local',
      syncCRDT: false  // Don't sync to CRDT yet, wait for backend confirmation
    });
    
    try {
      // Soft delete on backend (sets trashed: true)
      const result = await entityService.deleteEntity(entityId, false);
      
      log.info('[softDeleteEntity] Backend soft delete confirmed');
      
      // ✅ ARCHITECTURE: Use the confirmed entity from backend (if returned)
      // This ensures we have the exact backend state with trashed: true
      let confirmedEntity: BaseEntityType;
      
      if (result === true || result === false) {
        // Legacy boolean return - manually add trashed flag
        confirmedEntity = { ...entity, trashed: true };
        log.warn('[softDeleteEntity] Backend returned boolean, using manual trashed flag');
      } else {
        // Backend returned the updated entity - use it!
        const backendEntity = result as any;
        
        // ✅ CRITICAL: Backend returns 'id' instead of '_id' (Pydantic behavior)
        // Ensure _id is set so the registry / scoped stores key correctly.
        confirmedEntity = {
          ...backendEntity,
          _id: backendEntity._id || backendEntity.id || entityId,
        } as BaseEntityType;
      }
      
      // Apply confirmed backend update with proper CRDT sync
      upsertEntity(confirmedEntity, {
        log: true,       // Log for undo/redo
        source: 'local',
        syncCRDT: true   // Sync to CRDT now that backend confirmed
      });
      
      log.info('[softDeleteEntity] ✅ Entity soft deleted successfully');

      // Emit world event (fire-and-forget — never fatal)
      try {
        emitEvent('entity.deleted', { entityType: entity.entityType, name: entity.name }, undefined);
      } catch { /* non-fatal */ }

    } catch (error) {
      log.error('[softDeleteEntity] Failed to soft delete entity:', error);
      
      // Rollback: Restore original entity (without trashed flag)
      upsertEntity(entity, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      throw error;
    }
  },

  /**
   * Restore soft-deleted entity
   * Note: Entity may not be in store if it was hard-deleted, so we fetch from backend
   */
  undeleteEntity: async (entityId: string) => {
    log.info('[undeleteEntity] Restoring entity:', { entityId });
    
    try {
      // Restore on backend first (update entity to mark as not trashed)
      const restoredEntity = await entityService.update(entityId, { trashed: false } as any);
      
      // Add restored entity back to store
      upsertEntity(restoredEntity, {
        log: true,
        source: 'local',
        syncCRDT: true  // Sync to CRDT
      });
      
      log.info('[undeleteEntity] ✅ Entity restored successfully');
      
    } catch (error) {
      log.error('[undeleteEntity] Failed to restore entity:', error);
      throw error;
    }
  },

  // ========================================================================
  // SIMPLE OPERATIONS (Generic Entity Operations)
  // ========================================================================

  /**
   * Create a generic entity (no optimistic update)
   */
  createEntity: async (data: Partial<BaseEntityType>) => {
    const spaceId = data.spaceId || getActiveSpaceId() || '';
    log.info('[createEntity] ✅ spaceId resolved:', { spaceId, fromCaller: !!data.spaceId });
    
    // ✅ ORGS V1: Get current org context
    const { getCurrentOrgId } = await import('../stores/dataspaceContextStore');
    const currentOrgId = getCurrentOrgId();
    
    // ✅ FIX #3: Pre-flight validation to prevent invalid entities
    const { validateEntityBeforeCreate } = await import('../utils/entityValidation');
    const validation = validateEntityBeforeCreate({
      ...data,
      spaceId  // Use resolved spaceId for validation
    }, currentOrgId);
    
    if (!validation.valid) {
      const errorMessages = validation.errors.map(e => e.message).join(', ');
      const errorMsg = `Cannot create entity: ${errorMessages}`;
      
      log.error('[createEntity] ❌ Validation failed:', {
        entityType: data.entityType,
        name: data.name,
        errors: validation.errors
      });
      
      throw new Error(errorMsg);
    }
    
    // Log warnings (non-blocking)
    if (validation.warnings.length > 0) {
      log.warn('[createEntity] ⚠️ Validation warnings:', {
        entityType: data.entityType,
        name: data.name,
        warnings: validation.warnings
      });
    }
    
    log.info('[createEntity] Creating entity:', { entityType: data.entityType, name: data.name, spaceId, orgId: currentOrgId });

    // ── Offline path ─────────────────────────────────────────────────────────
    // When offline, write to the entity-system Y.Doc with a client-generated UUID
    // and queue the REST call for when the device comes back online.
    if (!navigator.onLine) {
      const tempId = `temp_${crypto.randomUUID()}`;
      const entityData: any = {
        ...data,
        _id: tempId,
        id: tempId,
        spaceId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        org_id: (data as any).org_id || currentOrgId || undefined,
        _pending: true,  // sidebar can show a pending indicator
      };

      const { writeEntityToYDoc } = await import('../stores/entitySystemYjsStore');
      const { enqueue } = await import('../services/offlineEntityQueue');

      writeEntityToYDoc(tempId, entityData);
      upsertEntity(entityData, { source: 'local', log: false, syncCRDT: false });
      enqueue({ id: tempId, type: 'entity', params: entityData, queuedAt: Date.now() });

      log.info(`[createEntity] 📴 Offline — queued with tempId: ${tempId.slice(-8)}`);
      return entityData as BaseEntityType;
    }
    // ─────────────────────────────────────────────────────────────────────────

    try {
      // Create on backend
      const entityData = {
        ...data,
        spaceId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as any;
      
      // ✅ ORGS V1: Set org_id from current context (handle both cases)
      entityData.org_id = (data as any).org_id || currentOrgId || undefined;
      
      const createdEntity = await entityService.create(entityData);
      
      log.info('[createEntity] Entity created:', { id: createdEntity._id, name: createdEntity.name });
      
      // Add to state via helper (handles normalization, CRDT sync, undo/redo)
      const normalized = upsertEntity(createdEntity, {
        log: true,
        source: 'local',
        syncCRDT: true
      });

      // Optimistic edge placement — mirrors the backend's AUTO-EDGE logic so the
      // entity appears in the correct sidebar position immediately, before the
      // Y.Doc broadcast round-trip completes.
      try {
        const { default: useDataspaceTreeStore } = await import('./dataspaceTreeStore');
        const treeStore = useDataspaceTreeStore.getState();
        if (treeStore.initialized && treeStore.dataspaceId) {
          const groupIds = (data.skeleton as any)?.groupIds;
          const parentId = (groupIds?.length > 0 ? groupIds[0] : null) ?? spaceId ?? null;
          treeStore.ensureNodeExists(normalized._id, parentId);
        }
      } catch { /* non-fatal — tree will recover via refreshEdges */ }

      // Emit event for external consumers
      entityEventService.entityCreated(normalized, spaceId, {
        command: 'createEntity'
      });

      // Emit world event (fire-and-forget — never fatal)
      try {
        emitEvent('entity.created', { entityType: normalized.entityType, name: normalized.name }, spaceId);
      } catch { /* non-fatal */ }

      return normalized;
      
    } catch (error) {
      log.error('[createEntity] Failed to create entity:', error);
      throw error;
    }
  },

  /**
   * Update an entity (no optimistic update)
   * 
   * ✅ Uses mergeUpdate to properly handle custom/arbitrary fields
   */
  updateEntity: async (entityId: string, data: Partial<BaseEntityType>) => {
    // ✅ ROBUSTNESS: Prevent saving entities from cache (data loss prevention)
    if ((data as any).__fromCache) {
      const error = new Error(
        `[updateEntity] ❌ PREVENTED DATA LOSS: Cannot save entity from cache! ` +
        `Entity ${entityId} is marked as cached (content stripped). ` +
        `Fetch fresh entity before updating.`
      );
      log.error(error.message);
      throw error;
    }
    
    log.info('[updateEntity] Updating entity:', { entityId, fields: Object.keys(data) });

    // ✅ OPTIMISTIC UPDATE: apply immediately so UI (sidebar, header) reflects the change
    // without waiting for the network round-trip. Follows the same pattern as createFolder.
    const currentEntity = findById(entityId);
    if (currentEntity) {
      log.info('[updateEntity] Applying optimistic update for instant UI feedback');
      upsertEntity({ ...currentEntity, ...data }, {
        log: false,       // Don't log temp state to undo stack
        source: 'local',
        syncCRDT: false,  // Don't sync yet — wait for backend confirmation
      });
    } else {
      log.warn('[updateEntity] Entity not found in store for optimistic update:', entityId);
    }
    
    try {
      // ✅ FIX: Use mergeUpdate instead of update for proper custom field handling
      // The /merge endpoint properly handles arbitrary fields like nodeCount, wordCount, etc.
      const updatedEntity = await entityService.mergeUpdate(entityId, {
        ...data,
        updated_at: new Date().toISOString()
      } as any);
      
      log.info('[updateEntity] Entity updated:', { id: updatedEntity._id });
      
      // Replace optimistic state with canonical backend response (handles normalization, CRDT sync, undo/redo)
      const normalized = upsertEntity(updatedEntity, {
        log: true,
        source: 'local',
        syncCRDT: true // Sync to CRDT for real-time collaboration
      });
      
      // ✅ EMIT ENTITY EVENT: Notify all subscribers of the update
      try {
        const { entityEventService } = await import('../services/entityEventService');
        const spaceId = normalized.spaceId || getActiveSpaceId() || '';
        entityEventService.entityUpdated(normalized, spaceId, {
          command: 'updateEntity'
        });
        log.info('[updateEntity] Entity event emitted:', { entityId: normalized._id, name: normalized.name });
      } catch (eventError) {
        log.error('[updateEntity] Failed to emit entity event:', eventError);
        // Don't throw - event emission failure shouldn't break the update
      }
      
      return normalized;
      
    } catch (error) {
      // Rollback: restore original entity so UI isn't left in a wrong state
      if (currentEntity) {
        log.warn('[updateEntity] Rolling back optimistic update due to backend error');
        upsertEntity(currentEntity, { log: false, source: 'local', syncCRDT: false });
      }
      log.error('[updateEntity] Failed to update entity:', error);
      throw error;
    }
  },

  /**
   * Get an entity (simple fetch)
   */
  getEntity: async (entityId: string) => {
    log.info('[getEntity] Fetching entity:', { entityId });
    
    try {
      const entity = await entityService.get(entityId);
      log.info('[getEntity] Entity fetched:', { id: entity._id, name: entity.name });
      return entity;
    } catch (error) {
      log.error('[getEntity] Failed to fetch entity:', error);
      throw error;
    }
  },

  // ========================================================================
  // COMPOSITE OPERATIONS
  // ========================================================================

  /**
   * Create folder and move multiple entities into it
   * Composite: mkdir + mv (multiple)
   */
  groupEntities: async (entityIds: string[], folderName: string, parentId?: string) => {
    const spaceId = getActiveSpaceId();
    const tempFolderId = `temp_group_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const oldState = snapshotAllEntities();
    
    log.info('[groupEntities] Grouping entities:', { entityIds, folderName, parentId, spaceId });
    
    // ✅ CRITICAL: Find folders that currently contain these entities (need to remove them)
    const oldParentFolders = oldState.filter((e: BaseEntityType) =>
      e.entityType === 'Group' &&
      (e.skeleton?.childEntities || []).some((childId: string) => entityIds.includes(childId))
    );
    
    log.info('[groupEntities] Found old parent folders:', oldParentFolders.map((f: BaseEntityType) => ({ id: f._id, name: f.name })));
    
    // ✅ CRITICAL: BATCH all optimistic updates into single store update
    // This prevents multiple intermediate hierarchy rebuilds with inconsistent state
    
    const entitiesToUpdate: BaseEntityType[] = [];
    
    // 0. Create temp folder
    const tempFolder: BaseEntityType = {
      _id: tempFolderId,
      id: tempFolderId,
      name: folderName.trim(),
      entityType: 'Group',
      type: 'group',
      spaceId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      skeleton: {
        '@type': 'Group',
        childEntities: entityIds,
        groupIds: parentId ? [parentId] : []  // ✅ Set groupIds if nested!
      }
    };
    
    entitiesToUpdate.push(tempFolder);
    
    // 1. Remove entities from old parents
    for (const oldParent of oldParentFolders) {
      const updatedChildEntities = (oldParent.skeleton?.childEntities || []).filter(
        (childId: string) => !entityIds.includes(childId)
      );
      
      entitiesToUpdate.push({
        ...oldParent,
        skeleton: {
          ...oldParent.skeleton,
          childEntities: updatedChildEntities
        }
      });
      
      log.info('[groupEntities] Optimistic: Will remove from old parent:', { parentId: oldParent._id, removed: entityIds.length });
    }
    
    // 2. If nested, add temp folder to parent's childEntities
    if (parentId) {
      const parentFolder = findById(parentId);
      if (parentFolder && parentFolder.entityType === 'Group') {
        const currentChildren = parentFolder.skeleton?.childEntities || [];
        if (!currentChildren.includes(tempFolderId)) {
          entitiesToUpdate.push({
            ...parentFolder,
            skeleton: {
              ...parentFolder.skeleton,
              childEntities: [...currentChildren, tempFolderId]
            }
          });
          
          log.info('[groupEntities] Optimistic: Will add temp folder to parent:', { parentId, tempFolderId });
        }
      }
    }
    
    // 3. Update all entities to point to temp folder
    for (const entityId of entityIds) {
      const entity = oldState.find((e: BaseEntityType) => e._id === entityId);
      if (entity) {
        entitiesToUpdate.push({
          ...entity,
          skeleton: {
            ...(entity.skeleton || {}),
            groupIds: [tempFolderId]
          }
        });
      }
    }
    
    // Apply all optimistic updates through the canonical pipeline
    for (const updatedEntity of entitiesToUpdate) {
      upsertEntity(updatedEntity, { source: 'local', log: false, syncCRDT: false });
    }
    
    log.info('[groupEntities] Applied immediate batch update (frontend state):', { 
      updatedCount: entitiesToUpdate.length,
      tempFolderId,
      addedNew: entitiesToUpdate.filter((e: BaseEntityType) => !oldState.find((old: BaseEntityType) => old._id === e._id)).length
    });
    
    try {
      // ✅ ORGS V1: Get current org context
      const { getCurrentOrgId } = await import('../stores/dataspaceContextStore');
      const currentOrgId = getCurrentOrgId();
      
      // Create folder on backend
      const folderData: any = {
        entityType: 'Group',
        name: folderName.trim(),
        spaceId,
        skeleton: {
          '@type': 'Group',
          childEntities: entityIds,
          groupIds: parentId ? [parentId] : []  // ✅ Set groupIds during creation!
        },
        created_at: new Date().toISOString(),
        org_id: currentOrgId || undefined,  // ✅ ORGS V1: Set org_id from current context
      };
      
      if (parentId) {
        folderData.parentId = parentId;
      }
      
      log.info('[groupEntities] Creating folder with org_id:', { orgId: currentOrgId, folderName });
      
      const createdFolder = await entityService.create(folderData);
      const actualFolderId = createdFolder._id || createdFolder.id;
      
      log.info('[groupEntities] Folder created:', { actualFolderId, name: createdFolder.name });
      
      // Edge arrives via org Y.Doc broadcast — no REST refresh needed
      
      // ✅ CRITICAL: Remove entities from old parent folders' childEntities
      for (const oldParent of oldParentFolders) {
        try {
          const currentChildEntities = oldParent.skeleton?.childEntities || [];
          const updatedChildEntities = currentChildEntities.filter(
            (childId: string) => !entityIds.includes(childId)
          );
          
          log.info(`[groupEntities] Removing entities from old parent ${oldParent._id}:`, {
            before: currentChildEntities.length,
            after: updatedChildEntities.length
          });
          
          await entityService.mergeUpdate(oldParent._id, {
            skeleton: {
              childEntities: updatedChildEntities
            }
          });
        } catch (error) {
          log.warn(`[groupEntities] Failed to update old parent ${oldParent._id}:`, error);
        }
      }
      
      // ✅ CRITICAL: If we have a parent folder, add the new group to its childEntities
      if (parentId) {
        try {
          const parentFolder = await entityService.get(parentId);
          const currentChildEntities = parentFolder.skeleton?.childEntities || [];
          
          if (!currentChildEntities.includes(actualFolderId)) {
            const updatedChildEntities = [...currentChildEntities, actualFolderId];
            
            log.info(`[groupEntities] Adding new group to parent ${parentId}:`, {
              before: currentChildEntities.length,
              after: updatedChildEntities.length
            });
            
            await entityService.mergeUpdate(parentId, {
              skeleton: {
                childEntities: updatedChildEntities
              }
            });
            
            // ✅ groupIds already set during folder creation above (line 607)
            // No need for separate update!
          }
        } catch (error) {
          log.warn(`[groupEntities] Failed to update parent folder ${parentId}:`, error);
        }
      }
      
      // Update each child entity to point to new group
      for (const childId of entityIds) {
        try {
          // ✅ SIMPLIFIED: Just set groupIds to the new group (replaces old parent)
          await entityService.mergeUpdate(childId, {
            skeleton: {
              groupIds: [actualFolderId]
            }
          });
        } catch (error) {
          log.warn(`[groupEntities] Failed to update child ${childId}:`, error);
        }
      }
      
      // ✅ NOTE: Backend auto-creates edges for all children when they're created
      // We already refreshed edges above, so tree store now has all the edges
      
      // ✅ CRITICAL: Update store with all changes via helpers
      
      // 1. Remove temp folder and add real folder
      deleteEntityLocal(tempFolderId, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      upsertEntity(createdFolder, {
        log: true,
        source: 'local',
        syncCRDT: true
      });
      
      // 2. Update moved entities to point to new parent
      for (const entityId of entityIds) {
        const entity = findById(entityId);
        if (entity) {
          // ✅ CRITICAL: Merge with FULL entity to preserve name, entityType, etc.
          upsertEntity({
            ...entity,  // Keep all existing fields!
            skeleton: {
              ...(entity.skeleton || {}),
              groupIds: [actualFolderId]
            }
          }, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
      }
      
      // 3. Confirm old parent folders are updated in store (fetch from backend)
      // The optimistic update already removed children, but we need to sync with backend state
      for (const oldParent of oldParentFolders) {
        try {
          // Fetch fresh from backend to get confirmed state
          // ✅ FIX: Ensure oldParent._id is a string before calling API
          const parentId = typeof oldParent._id === 'string' ? oldParent._id : String(oldParent._id);
          const freshParent = await entityService.get(parentId);
          
          upsertEntity(freshParent, {
            log: false,
            source: 'local',
            syncCRDT: false  // Already on backend
          });
          
          log.info('[groupEntities] Synced old parent from backend:', { parentId: oldParent._id });
        } catch (error) {
          log.warn(`[groupEntities] Failed to sync old parent ${oldParent._id}:`, error);
        }
      }
      
      // 4. Sync parent folder from backend (if nested)
      if (parentId) {
        try {
          // Fetch fresh from backend to get confirmed state with actual folder ID
          const freshParent = await entityService.get(parentId);
          
          upsertEntity(freshParent, {
            log: false,
            source: 'local',
            syncCRDT: false  // Already on backend
          });
          
          log.info('[groupEntities] Synced parent from backend:', { parentId, actualFolderId });
        } catch (error) {
          log.warn(`[groupEntities] Failed to sync parent folder ${parentId}:`, error);
        }
      }
      
      log.info('[groupEntities] ✅ Entities grouped successfully');
      
      return { folderId: actualFolderId, folder: createdFolder };
      
    } catch (error) {
      log.error('[groupEntities] Failed to group entities:', error);
      
      // Rollback: Remove temp folder, restore moved entities, and restore parent folders
      deleteEntityLocal(tempFolderId, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      for (const entityId of entityIds) {
        const originalEntity = oldState.find((e: BaseEntityType) => e._id === entityId);
        if (originalEntity) {
          upsertEntity(originalEntity, {
            log: false,
            source: 'local',
            syncCRDT: false
          });
        }
      }

      // Restore old parent folders' childEntities that were modified in the optimistic update
      for (const oldParent of oldParentFolders) {
        const originalParent = oldState.find((e: BaseEntityType) => e._id === oldParent._id);
        if (originalParent) {
          upsertEntity(originalParent, {
            log: false,
            source: 'local',
            syncCRDT: false,
          });
        }
      }
      
      throw error;
    }
  },

  /**
   * Move all entities out of folder and delete the folder
   * Composite: mv (all up) + rm
   * 
   * Moves children UP ONE LEVEL:
   * - If folder has a parent (is nested), children move to that parent
   * - If folder is at root level, children move to root
   */
  ungroupEntities: async (folderId: string) => {
    const oldState = snapshotAllEntities();
    
    log.info('[ungroupEntities] Ungrouping folder:', { folderId });
    
    const folder = oldState.find((e: BaseEntityType) => e._id === folderId);
    
    if (!folder) {
      log.warn('[ungroupEntities] Folder not found:', folderId);
      return;
    }
    
    // Find children of this folder
    const childEntityIds = folder.skeleton?.childEntities || [];
    const children = oldState.filter((e: BaseEntityType) => 
      childEntityIds.includes(e._id)
    );
    
    // Find the parent folder (if any) by checking folder's groupIds
    const folderGroupIds = folder.skeleton?.groupIds || [];
    const parentFolderId = folderGroupIds.length > 0 ? folderGroupIds[0] : null;
    
    log.info('[ungroupEntities] Found folder structure:', {
      folderId,
      folderName: folder.name,
      parentFolderId,
      childCount: children.length,
      childIds: childEntityIds
    });
    
    // ✅ BATCH: Prepare all immediate updates (prevents multiple rebuilds)
    const entitiesToUpdate: BaseEntityType[] = [];
    
    // 1. Mark folder as trashed (will be removed after backend confirms)
    entitiesToUpdate.push({ ...folder, trashed: true });
    
    // 2. Move children up one level (update their groupIds)
    for (const child of children) {
      entitiesToUpdate.push({ 
        ...child, 
        skeleton: {
          ...child.skeleton,
          groupIds: parentFolderId ? [parentFolderId] : []
        }
      });
    }
    
    // 3. If nested, update parent folder to include children
    if (parentFolderId) {
      const parentFolder = oldState.find((e: BaseEntityType) => e._id === parentFolderId);
      if (parentFolder) {
        const parentChildEntities = parentFolder.skeleton?.childEntities || [];
        const updatedParentChildren = parentChildEntities
          .filter((id: string) => id !== folderId)
          .concat(childEntityIds);
        
        entitiesToUpdate.push({
          ...parentFolder,
          skeleton: {
            ...parentFolder.skeleton,
            childEntities: updatedParentChildren
          }
        });
        
        log.info('[ungroupEntities] Will update parent folder:', {
          parentFolderId,
          before: parentChildEntities.length,
          after: updatedParentChildren.length
        });
      }
    }
    
    // ✅ CRITICAL: DON'T mark folder as trashed - go straight to delete
    // When undoing, we want to restore the folder in its original state (not trashed)
    
    // Move children up optimistically (not logged individually - folder restore handles this)
    for (const child of children) {
      upsertEntity({ 
        ...child, 
        skeleton: {
          ...child.skeleton,
          groupIds: parentFolderId ? [parentFolderId] : []
        }
      }, {
        log: false,  // Don't log child moves (folder restore handles this)
        source: 'local',
        syncCRDT: false
      });
    }
    
    // If nested, update parent folder optimistically (not logged - folder restore handles this)
    if (parentFolderId) {
      const parentFolder = oldState.find((e: BaseEntityType) => e._id === parentFolderId);
      if (parentFolder) {
        const parentChildEntities = parentFolder.skeleton?.childEntities || [];
        const updatedParentChildren = parentChildEntities
          .filter((id: string) => id !== folderId)
          .concat(childEntityIds);
        
        upsertEntity({
          ...parentFolder,
          skeleton: {
            ...parentFolder.skeleton,
            childEntities: updatedParentChildren
          }
        }, {
          log: false,
          source: 'local',
          syncCRDT: false
        });
      }
    }
    
    log.info('[ungroupEntities] Applied optimistic updates for undo/redo:', {
      folderMarkedTrashed: true,
      childrenMoved: children.length
    });
    
    try {
      // If folder has a parent, we need to:
      // 1. Remove this folder from parent's childEntities
      // 2. Add all children to parent's childEntities
      if (parentFolderId) {
        const parentFolder = await entityService.get(parentFolderId);
        const parentChildEntities = parentFolder.skeleton?.childEntities || [];
        
        // Remove the folder being deleted and add its children
        const updatedParentChildren = parentChildEntities
          .filter((id: string) => id !== folderId)
          .concat(childEntityIds);
        
        log.info('[ungroupEntities] Updating parent folder:', {
          parentFolderId,
          before: parentChildEntities,
          after: updatedParentChildren
        });
        
        await entityService.mergeUpdate(parentFolderId, {
          skeleton: {
            childEntities: updatedParentChildren
          }
        });
      }
      
      // Update each child's groupIds to point to parent (or empty for root)
      for (const child of children) {
        try {
          log.info('[ungroupEntities] Moving child up one level:', {
            childId: child._id,
            childName: child.name,
            newParent: parentFolderId || 'root'
          });
          
          await entityService.mergeUpdate(child._id, {
            skeleton: {
              groupIds: parentFolderId ? [parentFolderId] : []
            }
          });
        } catch (error) {
          log.warn(`[ungroupEntities] Failed to update child ${child._id}:`, error);
        }
      }
      
      // Delete folder on backend
      await entityService.deleteEntity(folderId);
      
      // ✅ CRITICAL: Remove folder from store immediately after backend confirms
      // The helper will capture previousData automatically (the trashed folder)
      deleteEntityLocal(folderId, {
        log: true,  // ✅ Log for undo! When undoing, folder will be restored
        source: 'local',
        syncCRDT: false  // Already deleted on backend
      });
      
      log.info('[ungroupEntities] Removed folder from store (logged for undo)');
      
      // Real-time sync handled by entity-system Y.Doc (broadcast from REST handler).
      log.info('[ungroupEntities] ✅ Folder ungrouped successfully');
      
    } catch (error) {
      log.error('[ungroupEntities] Failed to ungroup folder:', error);
      
      // Rollback via helpers
      upsertEntity(folder, {
        log: false,
        source: 'local',
        syncCRDT: false
      });
      
      for (const child of children) {
        const originalChild = oldState.find((e: BaseEntityType) => e._id === child._id);
        if (originalChild) {
          upsertEntity(originalChild, {
            log: false,
            source: 'local',
            syncCRDT: false
          });
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Add multiple entities to an existing group (batch operation)
   * Composite operation: single parent update + multiple child updates (parallel)
   * Use this for bulk operations like folder uploads to avoid event cascades
   */
  addEntitiesToGroup: async (entityIds: string[], groupId: string) => {
    log.info('[addEntitiesToGroup] Adding entities to group:', { entityIds, groupId, count: entityIds.length });
    
    try {
      // 1. Fetch fresh group data from backend
      const group = await entityService.get(groupId);
      if (!group || group.entityType !== 'Group') {
        throw new Error(`Group ${groupId} not found or not a valid group`);
      }
      
      const currentChildEntities = group.skeleton?.childEntities || [];
      const newEntityIds = entityIds.filter(id => !currentChildEntities.includes(id));
      
      if (newEntityIds.length === 0) {
        log.info('[addEntitiesToGroup] All entities already in group, nothing to do');
        return;
      }
      
      const updatedChildEntities = [...currentChildEntities, ...newEntityIds];
      
      log.info('[addEntitiesToGroup] Batch update:', {
        groupId,
        adding: newEntityIds.length,
        from: currentChildEntities.length,
        to: updatedChildEntities.length
      });
      
      // 2. Update parent group
      await entityService.mergeUpdate(groupId, {
        skeleton: {
          childEntities: updatedChildEntities
        }
      });
      
      // Update local cache immediately so the UI reflects the change without
      // waiting for the Y.Doc round-trip. mergeUpdate does not emit events.
      const freshGroup = await entityService.get(groupId);
      if (freshGroup) {
        upsertEntity(freshGroup, { source: 'local', log: false, syncCRDT: false });
      }
      
      // 3. Update all entities' groupIds in parallel
      await Promise.all(
        newEntityIds.map(entityId =>
          entityService.mergeUpdate(entityId, {
            skeleton: {
              groupIds: [groupId]
            }
          })
        )
      );
      
      // Refresh each modified entity's local groupIds so the sidebar reflects
      // the move without waiting on the Y.Doc broadcast.
      for (const entityId of newEntityIds) {
        const entity = findById(entityId);
        if (entity) {
          upsertEntity(
            { ...entity, skeleton: { ...(entity.skeleton as any), groupIds: [groupId] } },
            { source: 'local', log: false, syncCRDT: false }
          );
        }
      }
      
      log.info('[addEntitiesToGroup] ✅ Successfully added entities to group');
      
    } catch (error) {
      log.error('[addEntitiesToGroup] Failed to add entities to group:', error);
      throw error;
    }
  },
  
  /**
   * Close a hybrid window entity (Window/OpenDialog)
   * These entities represent both a UI window and an entity in the system
   * 
   * This is a convenience action that finds the window and closes it.
   * The actual entity deletion is handled by closeWindow action.
   */
  closeWindowEntity: async (entityId: string) => {
    const entity = findById(entityId);
    
    if (!entity) {
      log.warn('[closeWindowEntity] Entity not found:', entityId);
      return;
    }
    
    const isWindowEntity = entity.entityType === 'Window' || entity.entityType === 'OpenDialog';
    if (!isWindowEntity) {
      log.error('[closeWindowEntity] Entity is not a Window or OpenDialog:', {
        entityId,
        entityType: entity.entityType
      });
      throw new Error(`Entity ${entityId} is not a window entity (type: ${entity.entityType})`);
    }
    
    log.info('[closeWindowEntity] Closing hybrid window entity:', {
      entityId,
      entityType: entity.entityType,
      entityName: entity.name
    });
    
    try {
      // Find and close the window
      // Note: closeWindow will automatically soft-delete the Window entity
      const { default: useSpaceStore } = await import('./spaceStore');
      const spaceStore = useSpaceStore.getState();
      const windowId = Object.keys(spaceStore.windows).find(
        wId => spaceStore.windows[wId].entityId === entityId
      );
      
      if (windowId) {
        log.info('[closeWindowEntity] Found window, closing:', { windowId, entityId });
        await spaceStore.actions.closeWindow(windowId);
        log.info('[closeWindowEntity] ✅ Window closed (entity automatically deleted by closeWindow)');
      } else {
        log.warn('[closeWindowEntity] No window found for entity, falling back to direct soft delete:', entityId);
        // Fallback: If no window found, just soft-delete the entity directly
        // This shouldn't normally happen, but handles edge cases
        await entityService.deleteEntity(entityId, false);
        
        const trashedEntity = { ...entity, trashed: true };
        upsertEntity(trashedEntity, {
          log: true,
          source: 'local',
          syncCRDT: true
        });
      }
      
      log.info('[closeWindowEntity] ✅ Window entity closed successfully');
    } catch (error) {
      log.error('[closeWindowEntity] Failed to close window entity:', error);
      throw error;
    }
  },
};

// Per-action standalone exports for callers who prefer named imports
// (e.g. `import { createFolder } from '../stores/entityActions'`).
export const {
  createFolder,
  moveEntity,
  softDeleteEntity,
  undeleteEntity,
  createEntity,
  updateEntity,
  getEntity,
  groupEntities,
  ungroupEntities,
  addEntitiesToGroup,
  closeWindowEntity,
} = entityActions;